import React from 'react';
import { Upload, Trash2, ChevronRight, FileText, Square, CheckCircle } from 'lucide-react';

export const PlanCard = React.memo(({
    plan,
    planEntities,
    onSelectEntity,
    activePlanId,
    editingPlanId,
    handleDoubleClick,
    handleSetActive,
    tempName,
    setTempName,
    handleNameChange,
    handleKeyDown,
    canManageProject,
    triggerFileUpload,
    handleDeleteRequest,
    selectedEntityId,
    uploadProgress,
    isExpanded,
    onToggleExpand,
    draggedOverPlanId,
    handleDragEnterCard,
    handleDragLeaveCard,
    isAreaComplete,
}) => {
    const areaEntities = planEntities.filter(e => ['polygon', 'rect', 'ellipse'].includes(e.type));
    
    return (
        <div 
            data-plan-id={plan.id}
            className={`group relative border rounded-lg transition-all duration-200 ${
                plan.id === activePlanId
                ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                : 'dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            onDragEnter={(e) => handleDragEnterCard(e, plan.id)}
            onDragLeave={handleDragLeaveCard}
        >
            <div 
                onClick={() => !editingPlanId && handleSetActive(plan.id)}
                onDoubleClick={() => handleDoubleClick(plan)}
                className="p-3 cursor-pointer"
            >
                {editingPlanId === plan.id ? (
                    <input 
                        type="text"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        onBlur={handleNameChange}
                        onKeyDown={handleKeyDown}
                        className="w-full bg-transparent text-sm font-bold text-blue-800 dark:text-blue-300 outline-none p-0"
                        autoFocus
                    />
                ) : (
                    <div className="flex items-center space-x-2">
                        <FileText className={`h-4 w-4 flex-shrink-0 ${plan.url ? 'text-blue-500' : 'text-gray-400'}`} />
                        <p className="text-sm font-bold text-gray-700 dark:text-gray-300 truncate flex-grow">{plan.name}</p>
                        
                        <div className="flex items-center flex-shrink-0">
                            {canManageProject && (
                                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); triggerFileUpload(plan.id); }}
                                        className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                        aria-label={`Upload PDF for ${plan.name}`}
                                    >
                                        <Upload className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteRequest(plan); }}
                                        className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
                                        aria-label={`Delete ${plan.name}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                            {(areaEntities.length > 0 || plan.id === activePlanId) && (
                                <button onClick={(e) => { e.stopPropagation(); onToggleExpand(); }} className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600">
                                    <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : 'rotate-0'}`} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {plan.id === activePlanId && !editingPlanId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Active</p>
                )}
            </div>

            {draggedOverPlanId === plan.id && (
                <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-lg flex flex-col justify-center items-center pointer-events-none z-10">
                    <Upload className="h-6 w-6 text-blue-500" />
                    <p className="mt-1 text-xs font-semibold text-blue-600">Drop PDF here</p>
                </div>
            )}
            
            {isExpanded && areaEntities.length > 0 && (
                 <div className="pl-4 pr-2 pb-2 border-t dark:border-gray-600">
                    <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 my-2">Areas</h4>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                        {areaEntities.map(entity => {
                             const isComplete = isAreaComplete(entity.id);
                             return (
                                <li key={entity.id}>
                                    <button
                                        onClick={() => onSelectEntity(entity.id)}
                                        className={`w-full text-left text-sm p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 truncate flex items-center gap-2 ${selectedEntityId === entity.id ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
                                    >
                                        {isComplete
                                            ? <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                            : <Square className="h-4 w-4 flex-shrink-0" />
                                        }
                                        <span className="truncate">{entity.name}</span>
                                    </button>
                                </li>
                             );
                        })}
                    </ul>
                </div>
            )}


            {uploadProgress[plan.id] !== undefined && (
                <div className="absolute bottom-0 left-0 right-0 h-1 px-1 pb-1">
                    <div className="relative w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1">
                        <div className="bg-blue-600 h-1 rounded-full transition-all duration-150" style={{ width: `${uploadProgress[plan.id]}%` }}></div>
                    </div>
                </div>
            )}
        </div>
    );
});
